import { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase/config';

const API_BASE = process.env.REACT_APP_API_URL || 'https://gormaran-api.onrender.com';
let _cache = null;
let _cacheUid = null;

async function getToken() {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

async function apiFetch(path, method = 'GET', body) {
  const token = await getToken();
  if (!token) throw new Error('No auth');
  return fetch(`${API_BASE}/api/brand-memory${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  }).then(r => r.json());
}

export function useBrandMemory() {
  const [profile, setProfile] = useState(_cache);
  const [exists, setExists] = useState(!!_cache);
  const [loading, setLoading] = useState(!_cache);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) { setLoading(false); return; }
    if (_cache && _cacheUid === uid) { setLoading(false); return; }
    setLoading(true);
    apiFetch('/').then(data => {
      if (data?.exists && data.profile) {
        _cache = data.profile;
        _cacheUid = uid;
        setProfile(data.profile);
        setExists(true);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = useCallback(async (newProfile) => {
    setSaving(true);
    try {
      const data = await apiFetch('/', 'POST', { profile: newProfile });
      if (data.ok) {
        _cache = newProfile;
        _cacheUid = auth.currentUser?.uid;
        setProfile(newProfile);
        setExists(true);
      }
      return data.ok;
    } finally { setSaving(false); }
  }, []);

  const scrapeWebsite = useCallback(async (url) => {
    return apiFetch('/scrape', 'POST', { url });
  }, []);

  // Learning loop — 3 signals
  const learn = useCallback(async (signal, payload) => {
    // signal: 'approve' | 'edit' | 'discard'
    // payload: { content, canal, original?, edited?, topic? }
    try {
      await apiFetch('/learn', 'POST', { signal, ...payload });
      // Invalidate cache so next render gets updated profile
      _cache = null;
      _cacheUid = null;
    } catch {}
  }, []);

  // Legacy feedback (thumbs up/down)
  const sendFeedback = useCallback(async (type, content, context) => {
    if (type === 'approve' || type === 'like') return learn('approve', { content, canal: context?.channel });
    if (type === 'discard' || type === 'dislike') return learn('discard', { content, canal: context?.channel });
    if (type === 'edit') return learn('edit', { original: context?.original, edited: content, canal: context?.channel });
    try { await apiFetch('/feedback', 'POST', { type, content, context }); } catch {}
  }, [learn]);

  const qualityCheck = useCallback(async (content, channel) => {
    try { return await apiFetch('/quality-check', 'POST', { content, channel }); }
    catch { return null; }
  }, []);

  const invalidate = useCallback(() => {
    _cache = null;
    _cacheUid = null;
    setProfile(null);
    setExists(false);
  }, []);

  return { profile, exists, loading, saving, save, scrapeWebsite, learn, sendFeedback, qualityCheck, invalidate };
}
